import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "msc_customer_token";

const Ctx = createContext(null);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" · ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function buildClient(token) {
  return axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

export function CustomerAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!token) { setLoading(false); setCustomer(null); return () => {}; }
    buildClient(token).get("/customer/me")
      .then(r => { if (alive) setCustomer(r.data); })
      .catch(() => {
        if (!alive) return;
        // Token expired / invalid → clear
        localStorage.removeItem(TOKEN_KEY);
        setToken(""); setCustomer(null);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/customer/login`, { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setCustomer(data.customer);
    return data.customer;
  };

  const register = async (payload) => {
    const { data } = await axios.post(`${API}/customer/register`, payload);
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setCustomer(data.customer);
    return data.customer;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(""); setCustomer(null);
  };

  const updateProfile = async (payload) => {
    const { data } = await buildClient(token).put("/customer/me", payload);
    setCustomer(data);
    return data;
  };

  /**
   * Recharge le profil customer depuis l'API (utile après upload avatar/logo
   * partenaire ou changement d'infos partenaire).
   */
  const refreshCustomer = async () => {
    if (!token) return null;
    try {
      const { data } = await buildClient(token).get("/customer/me");
      setCustomer(data);
      return data;
    } catch { return null; }
  };

  const changePassword = async (current_password, new_password) => {
    await buildClient(token).post("/customer/change-password", { current_password, new_password });
  };

  // Addresses
  const listAddresses = async () => (await buildClient(token).get("/customer/addresses")).data;
  const createAddress = async (addr) => (await buildClient(token).post("/customer/addresses", addr)).data;
  const updateAddress = async (id, addr) => (await buildClient(token).put(`/customer/addresses/${id}`, addr)).data;
  const deleteAddress = async (id) => (await buildClient(token).delete(`/customer/addresses/${id}`)).data;
  const listOrders = async () => (await buildClient(token).get("/customer/orders")).data;

  return (
    <Ctx.Provider value={{
      token, customer, loading,
      isAuthenticated: !!customer,
      login, register, logout, updateProfile, refreshCustomer, changePassword,
      listAddresses, createAddress, updateAddress, deleteAddress, listOrders,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export const useCustomerAuth = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useCustomerAuth must be used within <CustomerAuthProvider>");
  return v;
};
